@if (displayToast()) {
  <div id="success-toast">
    <app-toast
      toastStyle="positive"
      icon="checkmark"
      text="Template deleted successfully"
    ></app-toast>
  </div>
}
@if (loading()) {
  <div class="loading-spinner--container" role="status" aria-live="polite">
    <app-loading-spinner></app-loading-spinner>
  </div>
} @else if (fetchTemplateError()) {
  <app-error-page
    title="Error"
    [primaryText]="fetchTemplateError()?.message"
  ></app-error-page>
} @else if (!laboratory()) {
  <app-error-page
    title="You are not a member of this lab"
    primaryText="Sorry, you cannot access this page unless you are a member of a lab"
  ></app-error-page>
} @else {
  <div id="page--container">
    <div id="title--container">
      <h4>Experiment Templates</h4>
      <h5>{{ laboratory()?.laboratoryName }}</h5>
    </div>
    <hr />
    <div id="table--container">
      <div class="button-two--container">
        <app-search-field
          [field]="searchField"
          [parentForm]="templateTableControls"
          (buttonClicked)="handleSearch($event)"
        >
        </app-search-field>
        <app-icon-button
          label="filters"
          buttonStyle="secondary"
          buttonId="filter-btn"
          iconName="filter_list"
          buttonShape="square"
          (buttonClicked)="openModal('filter')"
          title="Filters"
        ></app-icon-button>
        <app-mat-fab-button
          buttonStyle="accent"
          iconName="post_add"
          (buttonClicked)="onNewExperimentTemplate()"
          ariaLabel="New experiment template"
          [isDisabled]="!(currentUser()?.role | roleCheck: editAccessRoles)"
        ></app-mat-fab-button>
      </div>
      <table mat-table [dataSource]="templates()">
        <ng-container matColumnDef="name">
          <th
            mat-header-cell
            *matHeaderCellDef
            (click)="onSort('Name')"
            (keydown.enter)="onSort('Name')"
            [class.active]="currentSort().sortBy === 'Name'"
            [attr.aria-sort]="
              currentSort().sortBy === 'Name'
                ? currentSort().sortDirection === 'asc'
                  ? 'ascending'
                  : 'descending'
                : 'none'
            "
            tabindex="0"
            role="columnheader"
          >
            <span class="sortable">
              Name
              <span class="sort-icons" aria-hidden="true">
                <mat-icon>arrow_drop_up</mat-icon>
                <mat-icon>arrow_drop_down</mat-icon>
              </span>
            </span>
          </th>
          <td mat-cell *matCellDef="let t">
            <div class="name-status">
              {{ t.templateName }}
              @if (t.isDeleted) {
                <app-status-indicator status="Deleted"></app-status-indicator>
              }
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="mode">
          <th mat-header-cell *matHeaderCellDef>Mode</th>
          <td mat-cell *matCellDef="let t">
            {{ ExperimentModeDisplay[t.experimentMode] }}
          </td>
        </ng-container>

        <ng-container matColumnDef="modules">
          <th mat-header-cell *matHeaderCellDef>Modules</th>
          <td mat-cell *matCellDef="let t">
            @if (!t.modules) {
              <app-status-indicator status="None"></app-status-indicator>
            } @else {
              <app-active-modules
                [activeModules]="t.modules"
              ></app-active-modules>
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="description">
          <th mat-header-cell *matHeaderCellDef>Description</th>
          <td mat-cell *matCellDef="let t">{{ t.description }}</td>
        </ng-container>

        <ng-container matColumnDef="createdBy">
          <th mat-header-cell *matHeaderCellDef>Created By</th>
          <td mat-cell *matCellDef="let t">{{ t.createdBy }}</td>
        </ng-container>

        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef>Actions</th>
          <td mat-cell *matCellDef="let t">
            <div
              class="actions-cell"
              (click)="$event.stopPropagation()"
              (keydown.enter)="$event.stopPropagation()"
            >
              @if (t.isDeleted) {
                <app-mat-icon-button
                  [buttonId]="'view-btn-' + t.id"
                  iconName="chevron_right"
                  ariaLabel="View Template"
                  (buttonClicked)="editExperimentTemplate(t.id)"
                  [isDisabled]="
                    !(currentUser()?.role | roleCheck: editAccessRoles)
                  "
                ></app-mat-icon-button>
              } @else {
                <app-mat-icon-button
                  [buttonId]="'edit-btn-' + t.id"
                  iconName="edit"
                  ariaLabel="Edit Template"
                  (buttonClicked)="editExperimentTemplate(t.id)"
                  [isDisabled]="
                    !(currentUser()?.role | roleCheck: editAccessRoles)
                  "
                ></app-mat-icon-button>
              }
              <app-mat-icon-button
                [buttonId]="'duplicate-btn-' + t.id"
                iconName="content_copy"
                ariaLabel="Duplicate Template"
                (buttonClicked)="duplicateExperimentTemplate(t.id)"
                [isDisabled]="
                  !(currentUser()?.role | roleCheck: editAccessRoles) ||
                  t.isDeleted
                "
              ></app-mat-icon-button>
              <app-mat-icon-button
                buttonStyle="negative"
                [buttonId]="'delete-btn-' + t.id"
                ariaLabel="Delete Template"
                iconName="delete"
                (buttonClicked)="deleteTemplateConfirmation(t.id)"
                [isDisabled]="
                  !(currentUser()?.role | roleCheck: editAccessRoles) ||
                  t.isDeleted
                "
              ></app-mat-icon-button>
              <app-mat-outlined-button
                label="Use"
                ariaLabel="Use Template"
                [buttonId]="'use-btn-' + t.id"
                iconName="arrow_right"
                iconPosition="left"
                (buttonClicked)="useTemplate(t.id)"
                [isDisabled]="
                  !(currentUser()?.role | roleCheck: editAccessRoles) ||
                  t.isDeleted
                "
              >
              </app-mat-outlined-button>
            </div>
          </td>
        </ng-container>
        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr
          mat-row
          *matRowDef="let row; columns: displayedColumns"
          class="clickable-row"
          tabindex="0"
          (click)="editExperimentTemplate(row.id)"
          (keydown.enter)="editExperimentTemplate(row.id)"
        ></tr>
        <tr *matNoDataRow>
          <td [attr.colspan]="displayedColumns.length" id="empty-state">
            <mat-icon id="empty-state--icon">article</mat-icon>
            <p id="empty-state--title">No experiment templates</p>
            <p id="empty-state--subtitle">
              Create a new template to get started
            </p>
          </td>
        </tr>
      </table>
      <div id="pagination--container">
        <div class="filter--container">
          <app-dropdown-form-field
            id="templates-per-page"
            [field]="templatesPerPageField"
            [parentForm]="templateTableControls"
            dropdownStyle="horizontal"
          >
          </app-dropdown-form-field>
        </div>
        <div id="pagination--controls">
          <app-icon-button
            iconName="chevron_left"
            buttonId="previous"
            ariaLabel="Previous page"
            (buttonClicked)="getPrevious()"
            [isDisabled]="!hasPrevious()"
          ></app-icon-button>
          <span> {{ currentPage() }} / {{ totalPages() }} </span>
          <app-icon-button
            iconName="arrow_forward"
            buttonId="next"
            ariaLabel="Next page"
            (buttonClicked)="getNext()"
            [isDisabled]="!hasNext()"
          ></app-icon-button>
        </div>
      </div>
    </div>
  </div>
}
<app-filter-modal
  id="filter"
  primaryButtonText="Apply Filters"
  primaryButtonId="continue-button"
  secondaryButtonText="Reset"
  secondaryButtonId="reset-button"
  [modulesField]="experimentModuleFilters"
  [statusField]="experimentModeFilters"
  [deletedField]="includeDeletedTemplatesField"
  [parentForm]="templateTableControls"
  [hideDeletedField]="
    !(currentUser()?.role | roleCheck: [Role.OGI_Super_Administrator])
  "
  (modalButtonClicked)="modalButtonClicked($event)"
  (moduleCheckboxClicked)="moduleCheckboxClicked($event)"
  [isProcessing]="processing()"
></app-filter-modal>
<app-validation-modal
  id="delete-template-confirmation"
  titleText="Delete template?"
  validationText="delete"
  primaryButtonText="Confirm delete"
  primaryButtonId="confirm-delete-button"
  secondaryButtonText="Cancel"
  secondaryButtonId="cancel-delete-button"
  (modalButtonClicked)="modalButtonClicked($event)"
  [error]="deleteTemplateError()"
></app-validation-modal>
<app-modal
  id="delete-error"
  titleText="Unable to delete template"
  [primaryText]="deleteTemplateError()?.message"
  primaryButtonText="Close"
  primaryButtonId="error-primary-button"
  modalStyle="error"
  (modalButtonClicked)="modalButtonClicked($event)"
  [allowClose]="false"
>
</app-modal>
